import { useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { AlertMessage } from "@/components/ui/alert-message";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useOrganization } from "@/context/OrganizationContext";
import { languageName } from "@/constants/languages";
import {
  CATALOGUES_CACHE_KEY,
  CatalogueService,
  catalogueCacheKey,
  type Catalogue,
} from "@/services/catalogue.service";

const PROCESSING_FEATURE_LABELS = [
  { id: "imageSearch", label: "Image search" },
  { id: "semanticTextIndexing", label: "Semantic text indexing" },
  { id: "aiProcessing", label: "AI processing" },
  { id: "moderation", label: "Moderation" },
  { id: "deduplication", label: "Deduplication" },
] as const;

interface CatalogueSettingsProps {
  catalogue: Catalogue;
  onUpdated: (catalogue: Catalogue) => void;
}

export function CatalogueSettings({ catalogue, onUpdated }: CatalogueSettingsProps) {
  const { can } = useOrganization();
  const { mutate } = useSWRConfig();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const canManageCatalogue = can("catalog");
  const ready = catalogue.provisioningStatus === "ready" && !catalogue.deletedAt && !catalogue.purgedAt;
  const enabled = catalogue.processingFeatures.roomVisualiserDataExtraction;
  const { data: features, error: featureError, isLoading: loadingFeatures } = useSWR(
    canManageCatalogue && ready ? ["catalogue-processing-features", catalogue.id] : null,
    CatalogueService.getFeatures,
  );
  const entitled = features?.find((feature) => feature.id === "roomVisualiserDataExtraction")?.allowed === true;
  const supportsTranslation = catalogue.capabilities.includes("translation");

  const saveExtraction = async (selected: boolean) => {
    // Disabling remains available after an entitlement is withdrawn.
    if (!canManageCatalogue || !ready || isSaving || (selected && !entitled)) return;
    setIsSaving(true);
    setSaveError(null);
    setSavedMessage(null);
    try {
      const state = await CatalogueService.setRoomVisualiserExtraction(catalogue.id, selected);
      const updated = { ...catalogue, processingFeatures: state.processingFeatures, capabilities: state.capabilities };
      onUpdated(updated);
      await mutate(catalogueCacheKey(catalogue.id), updated, { revalidate: false });
      // The successful write is authoritative even if cache revalidation fails.
      void mutate(CATALOGUES_CACHE_KEY).catch(() => undefined);
      setSavedMessage(state.processingFeatures.roomVisualiserDataExtraction
        ? "Saved. Products are classified as they are ingested; existing products on their next ingest."
        : "Saved. New ingests skip extraction. Extracted data is kept and source data continues to ingest.");
    } catch (error: unknown) {
      setSaveError(error instanceof Error ? error.message : "Could not save the extraction setting");
      void mutate(catalogueCacheKey(catalogue.id)).catch(() => undefined);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-4">
      <Card className="overflow-hidden">
        <CardHeader className="border-b bg-muted/20">
          <h2 className="text-lg font-semibold leading-none tracking-tight">Processing features</h2>
          <CardDescription>
            Room Visualiser Data Extraction can be changed below. Other processing features are fixed at creation.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2 p-6 sm:grid-cols-2">
          {PROCESSING_FEATURE_LABELS.map(({ id, label }) => (
            <div key={id} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
              <span>{label}</span>
              <span className={catalogue.processingFeatures[id] ? "text-emerald-600" : "text-muted-foreground"}>
                {catalogue.processingFeatures[id] ? "Enabled" : "Not selected"}
              </span>
            </div>
          ))}
          <div className="space-y-3 rounded-md border p-4 sm:col-span-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="room-visualiser-data-extraction" className="font-medium">Room Visualiser Data Extraction</Label>
                <p className="mt-1 text-sm text-muted-foreground">
                  Classify home products by room type, style and placement during ingest, for Room Visualiser.
                </p>
              </div>
              <Switch
                id="room-visualiser-data-extraction"
                aria-label="Room Visualiser Data Extraction"
                checked={enabled}
                disabled={!canManageCatalogue || !ready || isSaving || (!enabled && (!entitled || loadingFeatures || !!featureError))}
                onCheckedChange={(selected) => void saveExtraction(selected)}
              />
            </div>
            <p className="text-sm text-muted-foreground">{enabled ? "Enabled" : "Not selected"}</p>
            {!canManageCatalogue ? <p className="text-sm text-muted-foreground">Catalogue permission is required to change this setting.</p>
              : !ready ? <p className="text-sm text-muted-foreground">This setting can be changed on a ready, active catalogue.</p>
                : loadingFeatures ? <p className="text-sm text-muted-foreground">Checking your plan…</p>
                  : featureError ? <p role="alert" className="text-sm text-red-600">Could not check your plan. Reload to try again.</p>
                    : !entitled ? <p className="text-sm text-muted-foreground">Not in your plan. {enabled ? "You can still switch extraction off." : ""}</p> : null}
            {isSaving ? <p role="status" className="flex items-center gap-2 text-sm"><Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />Saving…</p> : null}
            <AlertMessage message={saveError} type="error" />
            {savedMessage ? <p role="status" className="text-sm text-muted-foreground">{savedMessage}</p> : null}
            {!enabled ? <p className="text-sm text-muted-foreground">Off by default. When on, products are classified as they are ingested; existing products are classified on their next ingest.</p> : null}
          </div>
        </CardContent>
      </Card>

      {supportsTranslation ? (
        <Card className="overflow-hidden">
          <CardHeader className="border-b bg-muted/20">
            <h2 className="text-lg font-semibold leading-none tracking-tight">Catalogue languages</h2>
            <CardDescription>
              Languages are fixed when the catalogue is created and define every required localized taxonomy field.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2 p-6">
            {catalogue.languages.map((language) => (
              <span
                key={language}
                className="rounded-full border bg-muted/30 px-3 py-1.5 text-sm"
              >
                {languageName(language)} ({language})
              </span>
            ))}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
